import React, { useEffect, useRef, useState } from 'react';
import Drawer from 'react-modern-drawer';
import { useAuth } from '../../../../modules/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import db from '../../../../../services/firebase';
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast';
import DrawerHeader from '../drawer-components/DrawerHeader';
import { DrawerContent } from '../drawer-components/DrawerContent';
import Overlay from 'app/components/Overlay';
import DrawerFooter from '../drawer-components/DrawerFooter';
import InfoCard from '../drawer-components/InfoCard';
import { AddDrawerIntegrationProps } from '../utils/types';



const AIKIDO_CLIENT_ID = process.env.REACT_APP_AIKIDO_CLIENT_ID || '';
const AIKIDO_REDIRECT_URI = process.env.REACT_APP_AIKIDO_REDIRECT_URI || '';
const AIKIDO_URL = 'https://app.aikido.dev';

const AikidoSecurityDrawer: React.FC<AddDrawerIntegrationProps> = ({ id, isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(false);
  const [openAccordion2, setOpenAccordion2] = useState(false);
  const [aikidoAccessToken, setAikidoAccessToken] = useState('');
  const { currentUser } = useAuth();
  const [showAlert, setShowAlert] = useState(false);
  const drawerTopRef = useRef<HTMLDivElement>(null);
  const [integration, setIntegration] = useState<any[]>([]);
  const isConnected = integration.length > 0 && integration[0]?.status === 'Connected';

  const [isSyncingVulnerabilities, setIsSyncingVulnerabilities] = useState(false);
  const [accessTokenAikido, setAccessTokenAikido] = useState<string | null>(
    localStorage.getItem('aikido_access_token')
  );

  useEffect(() => {
    if (isOpen && id) {
      getIntegration();
    }
  }, [id, isOpen]);

  useEffect(() => {
    if (isConnected && integration[0]) {
      setAikidoAccessToken(integration[0].accessToken || '');
    } else {
      setAikidoAccessToken('');
    }
  }, [integration, isConnected]);

  const getIntegration = () => {
    try { 
      const tenantId = currentUser?.tenantId || '';
      if (!id) return;
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id);
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          if (data.name === 'Aikido Security') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              accessToken: data.accessToken,
            };
            setIntegration([integrationData]);
            setAccessTokenAikido(data.accessToken);
          }
        }
      });
      return unsubscribe;
    } catch (error) {
      console.error('Error getting integration:', error);
    }
  };

  useEffect(() => {
    if (showAlert && drawerTopRef.current) {
      drawerTopRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showAlert]);

  const handleConnect = async () => {
    if (!accessTokenAikido) {
      setIsLoading(true);
              localStorage.setItem('pendingIntegration', 'Aikido Security');
      const encodedRedirectUri = encodeURIComponent(AIKIDO_REDIRECT_URI);
      const authUrl = `${AIKIDO_URL}/oauth/authorize?client_id=${AIKIDO_CLIENT_ID}&redirect_uri=${encodedRedirectUri}&response_type=code&scope=read:security write:security`;
      window.location.href = authUrl;
      return;
    }
  };

  const currentDate = new Date();
  const formattedDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(currentDate.getDate()).padStart(2, '0')}`;

  const handleClose = () => {
    setShowAlert(false);
    onClose();
  };



  const handleDisconnect = async () => {
    try {
      setIsLoading(true);
      const tenantId = currentUser?.tenantId || '';
      if (!id || !tenantId) return;
      const tenantRef = doc(db, 'tenants', tenantId);

      const assetsRef = collection(tenantRef, 'assets');
      const aikidoAssetsQuery = query(assetsRef, where('platform', '==', 'Aikido Security'));
      const aikidoAssetsSnapshot = await getDocs(aikidoAssetsQuery);

      const deleteAssetPromises = aikidoAssetsSnapshot.docs.map(async (assetDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'assets', assetDoc.id));
      });

      await Promise.all(deleteAssetPromises);

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id);
      await deleteDoc(integrationRef);

      const vendorsRef = collection(tenantRef, 'vendors');
      const aikidoVendorQuery = query(vendorsRef, where('vendor_name', '==', 'Aikido Security'));
      const aikidoVendorSnapshot = await getDocs(aikidoVendorQuery);

      const deleteVendorPromises = aikidoVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id));
      });

      await Promise.all(deleteVendorPromises);

      localStorage.removeItem('aikido_access_token');

      const activitiesRef = collection(tenantRef, 'activities');
      const currentDate = new Date();
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`;
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected Aikido Security integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      });

      showSuccessToast({ message: 'Aikido Security integration disconnected' });

      setIntegration([]);
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error disconnecting Aikido Security:', errorMessage);
      showErrorToast({ message: 'Failed to disconnect Aikido Security' });
    } finally {
      setIsLoading(false);
    }
  };



  // const handleSyncVulnerabilities = async () => {
  //   if (!isConnected) {
  //     showErrorToast({ message: 'Please connect to Aikido first' });
  //     return;
  //   }

  //   setIsSyncingVulnerabilities(true);
  //   try {
  //     const tenantId = currentUser?.tenantId || '';
  //     if (!tenantId) {
  //       showErrorToast({ message: 'No tenant ID found' });
  //       return;
  //     }

  //     const tenantRef = doc(db, 'tenants', tenantId);
  //     const integrationsRef = collection(tenantRef, 'integrations');
  //     const aikidoIntegrationQuery = query(integrationsRef, where('name', '==', 'Aikido'));
  //     const aikidoIntegrationSnapshot = await getDocs(aikidoIntegrationQuery);

  //     if (aikidoIntegrationSnapshot.empty) {
  //       showErrorToast({ message: 'Aikido integration not found. Please reconnect.' });
  //       return;
  //     }

  //     const aikidoIntegrationData = aikidoIntegrationSnapshot.docs[0].data();
  //     const aikidoAccessToken = aikidoIntegrationData.accessToken;
  //     const integrationId = aikidoIntegrationSnapshot.docs[0].id;
      
  //     if (!aikidoAccessToken) {
  //       showErrorToast({ message: 'No access token found for Aikido integration.' });
  //       return;
  //     }

  //     const aikidoIntegration = createAikidoIntegration(aikidoAccessToken);
  //     // const syncResult = await aikidoIntegration.syncVulnerabilities(tenantId, integrationId);
      
  //     if (syncResult.success) {
  //       showSuccessToast({ 
  //         message: `Successfully synced ${syncResult.totalCount} vulnerabilities (${syncResult.newCount} new, ${syncResult.resolvedCount} resolved)` 
  //       });
  //     } else {
  //       showErrorToast({ message: `Failed to sync vulnerabilities: ${syncResult.error}` });
  //     }
  //   } catch (error) {
  //     console.error('Error syncing vulnerabilities:', error);
  //     showErrorToast({ message: 'Failed to sync vulnerabilities' });
  //   } finally {
  //     setIsSyncingVulnerabilities(false);
  //   }
  // };

  return (
    <Drawer open={isOpen} onClose={handleClose} direction="right" className="" size="45%">
      <DrawerHeader title="Aikido Security" onClose={onClose} />

      <div
        className="scroll-y overflow-auto p-6 mb-10"
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
        <div className="form">
          <DrawerContent
            title="Aikido Security"
            description="Vulnerability Management and Security Scanning"
            logo="/integrations/aikido.png"
            integration={integration}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            openAccordion2={openAccordion2}
            setOpenAccordion2={setOpenAccordion2}
            permissionInfo="SecureSlate will be granted permissions to read and write security scan data via Aikido's OAuth 2.0, respecting user permissions."
            accessTypeinfo="Requires an active Aikido integration with sufficient permissions to access repository and scan data."
          />

          <>
            <div className="mb-8 d-flex justify-content-between align-items-center">
              <div className="fs-6 fw-bold text-bold">
                {isConnected ? 'Aikido Security Connected to SecureSlate' : 'Connect Aikido Security to SecureSlate'}{' '}
                <Overlay
                  content="Upon successful connection, all repositories within your Aikido account will be automatically imported and presented within the Asset Repository section of the platform for your review and management."
                />
              </div>
              {/* {isConnected && (
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline fs-6"
                    // onClick={handleSyncVulnerabilities}
                    disabled={isSyncingVulnerabilities}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f1f1')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M12 9v2m0 4v.01" />
                      <path d="M5 19h14a2 2 0 0 0 1.84 -2.75L13.74 4a2 2 0 0 0 -3.5 0l-7.1 12.25a2 2 0 0 0 1.75 2.75" />
                    </svg>
                    {isSyncingVulnerabilities ? (
                      <>
                        Syncing Vulnerabilities...
                        <span className="spinner-border spinner-border-sm align-middle me-2"></span>
                      </>
                    ) : (
                      'Sync Vulnerabilities'
                    )}
                  </button>
                </div>
              )} */}
            </div>
            <InfoCard
              type="info"
              title="Connect Aikido Security to SecureSlate"
              description="Click on the 'Connect' button below. You will be redirected to Aikido to authenticate via OAuth and authorize SecureSlate. Once authorized, repositories will be automatically fetched and configured."
            />
          </>
        </div>
      </div>
      <DrawerFooter
        isConnected={isConnected}
        isLoading={isLoading}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        customActions={!isConnected}
      />
    </Drawer>
  );
};

export default AikidoSecurityDrawer;